import { useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { useObservationLogger } from '../useObservationLogger';

export const PRACTICE_PROBLEM_CHECKED_EVENT = 'practice_problem_checked';

/** `observation_type` of a checked problem, suffixed with its 1-based position in the set. */
export const practiceProblemObservationType = (questionIndex: number): string =>
  `practice_problem_${questionIndex}_correct`;

/** The payload as PostHog receives it: snake_case keys. */
export interface PracticeProblemCheckedProperties {
  /** Learnosity session id for this practice set attempt. */
  session_id: string;
  /** The skill the practice set belongs to, as its business code (for example `391`). */
  skill_code: string;
  /** 1-based position in the set, matching the header's "Problem N of M". */
  question_index: number;
  /** Size of the set, so an index is readable without a second lookup. */
  question_total: number;
  /** Whether the checked problem scored full marks. */
  is_correct: boolean;
}

export type CapturePracticeProblemChecked = (properties: PracticeProblemCheckedProperties) => void;

/**
 * The single capture point for "Check answer" in the practice problems experiment. Both the event
 * name and the payload are typed so a call site can neither invent an event nor forget a property.
 *
 * Fans out to two sinks that answer different questions. PostHog keeps the attempt intact — session
 * and skill included — for per-student product analytics. `experiments_observations` keeps a
 * correct/incorrect per problem position under `skill_metrics`, for the QuickSight dashboards that
 * read Postgres.
 *
 * Skipped in preview mode so a teacher looking at a student view never registers as an attempt.
 *
 * @param experimentVariant `useFeatureFlagVariantKey`'s return value, forwarded from SkillResource
 * so the flag is still read in exactly one place. Used only to tell enrolled students apart; the
 * arm itself is not written to the row.
 */
export const usePracticeProblemCheckEvent = (
  experimentVariant: string | boolean | undefined,
): CapturePracticeProblemChecked => {
  const postHog = usePostHog();
  const previewMode = usePreviewMode();
  const { logObservations } = useObservationLogger();

  // Enrollment gate only: a string means the student is in an arm. Which arm is no longer stored —
  // experiments_flag_history resolves that at analysis time — but the control component is also
  // what an unenrolled student sees, and their practice belongs to no experiment.
  const isEnrolled = typeof experimentVariant === 'string';

  return useCallback<CapturePracticeProblemChecked>(
    properties => {
      if (previewMode) return;

      // Captured for everyone who practises, enrolled or not — product analytics wants the whole
      // picture and the extra volume is free there, unlike in Postgres.
      postHog.capture(PRACTICE_PROBLEM_CHECKED_EVENT, properties);

      if (!isEnrolled) return;

      // Named by skill rather than by flag, matching AssessmentWrapper's rows: the experiment arm is
      // resolved at analysis time, which frees variant_name for the skill code. Fire and forget —
      // the logger reports its own failures and nothing here is retried.
      logObservations(
        'skill_metrics',
        [
          {
            subject: properties.skill_code,
            type: practiceProblemObservationType(properties.question_index),
            value: properties.is_correct ? 1 : 0,
          },
        ],
        { sendToPostHog: false },
      );
    },
    [postHog, previewMode, logObservations, isEnrolled],
  );
};
